import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Info } from "lucide-react";
import { api } from "@/lib/api";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/site/ProductCard";
import { MaskLine } from "@/components/site/Reveal";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { Faq, faqLd } from "@/components/site/Faq";
import { inline } from "@/components/site/RichText";
import { useSeo, SITE_URL, absUrl, ORGANIZATION, FOUNDER } from "@/lib/seo";
import { TOPICS, TOPIC_LINKS, CHUTE_LINKS, EXPERT_LINKS, SHIP } from "@/lib/topics";
import { ChuteGuidesNav, GuideOffer, PinButton } from "@/components/site/ChuteGuides";
import { Testimonials } from "@/components/home/Testimonials";
import { ExpertiseBlock } from "@/components/site/Expertise";
import { COLLECTION_LINKS } from "@/lib/collections";
import { CureBanner } from "@/components/site/CureBanner";
import { PregnancyNotice, hasRepouss, mentionsRepouss } from "@/components/site/PregnancyNotice";
import { SubGuideBanner } from "@/components/subscription/SubPromo";
import { FormationBox } from "@/components/site/FormationBox";
import { FormationGuideCta, FormationGuidesNav } from "@/components/formation/FormationGuideCta";
import { FORMATION_GUIDE_LINKS } from "@/lib/formationGuides";

const LABELS = { ...Object.fromEntries([...TOPIC_LINKS, ...CHUTE_LINKS, ...EXPERT_LINKS, ...FORMATION_GUIDE_LINKS, ...COLLECTION_LINKS].map((l) => [l.to, l.label])), "/formation": "La formation Méthode Ozentya" };
const CHUTE_HUB = "chute-de-cheveux-alopecie";
const frDate = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

function GuideByline({ updated }) {
  return (
    <p data-testid="topic-byline" className="mt-6 text-sm text-[var(--parchment)]/85">
      Par <Link to="/a-propos" data-testid="topic-byline-author" className="text-[var(--gold-soft)] underline underline-offset-4">Pascal Chaubo</Link>, fondateur d'Ozentya (soins cheveux afro depuis 2011)
      {updated ? <> · <time dateTime={updated}>mis à jour le {frDate(updated)}</time></> : null}
    </p>
  );
}

function ExpertGuidesNav({ current }) {
  return (
    <nav data-testid="expert-guides-nav" aria-label="Guides experts" className="mt-20">
      <h2 className="eyebrow mb-5">Nos guides experts</h2>
      <div className="flex flex-wrap gap-2">
        {EXPERT_LINKS.filter((l) => l.to !== current).map((l) => (
          <Link key={l.to} to={l.to} data-testid={`expert-guide-link-${l.to.slice(1)}`} className="flex min-h-10 items-center rounded-full border border-[var(--gold)]/40 px-5 py-2 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">{l.label}</Link>
        ))}
        <Link to="/glossaire-cheveux-afro" data-testid="expert-guide-link-glossaire" className="flex min-h-10 items-center rounded-full border border-[var(--gold)] px-5 py-2 text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)] transition-colors hover:bg-[rgba(217,180,106,0.12)]">Glossaire cheveux afro</Link>
      </div>
    </nav>
  );
}

export function useArticles(hub) {
  return useQuery({
    queryKey: ["articles", hub || "all"],
    queryFn: async () => { const r = await api.get("/articles", { params: hub ? { hub } : {} }); return Array.isArray(r.data) ? r.data : []; },
    staleTime: 60_000,
  });
}

export function ArticleCards({ articles, testid = "related-articles" }) {
  if (!articles?.length) return null;
  return (
    <div data-testid={testid} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((a) => (
        <Link key={a.slug} to={`/conseils/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group block rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6 transition-colors duration-300 hover:border-[var(--gold)]">
          <p className="eyebrow mb-3">Conseils</p>
          <h3 className="font-display text-2xl leading-snug group-hover:text-[var(--gold-soft)]">{a.title}</h3>
          <p className="mt-3 line-clamp-3 text-sm text-[var(--parchment)]/80">{a.excerpt}</p>
          <span className="mt-4 flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]">Lire l'article <ArrowUpRight className="h-3.5 w-3.5" /></span>
        </Link>
      ))}
    </div>
  );
}

export default function TopicPage({ id }) {
  const t = TOPICS[id];
  const { data } = useProducts();
  const all = Array.isArray(data) ? data : [];
  const products = t.products.slugs ? t.products.slugs.map((s) => all.find((p) => p.slug === s)).filter(Boolean) : all.filter((p) => p.category === t.products.category);
  const articles = useArticles(t.path).data || [];
  const chute = t.group === "chute" || id === CHUTE_HUB;
  const repouss = chute || products.some(hasRepouss) || mentionsRepouss(t);
  const crumbs = [{ to: "/", label: "Accueil" }, ...(t.group === "chute" ? [{ to: `/${CHUTE_HUB}`, label: TOPICS[CHUTE_HUB].name }] : []), ...(t.group === "expert" ? [{ to: "/conseils", label: "Conseils" }] : []), ...(t.group === "formation" ? [{ to: "/formation", label: "Formation" }] : []), { to: t.path, label: t.name }];
  useSeo({
    title: t.title, description: t.description, path: t.path, image: t.image?.src || products[0]?.image,
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "CollectionPage", name: t.h1, url: `${SITE_URL}${t.path}`, description: t.description, publisher: ORGANIZATION,
        mainEntity: { "@type": "ItemList", itemListElement: products.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/produit/${p.slug}`, name: p.name, image: absUrl(p.image) })) } },
      breadcrumbLd(SITE_URL, crumbs), faqLd(t.faq),
      { "@type": "Article", headline: t.h1, description: t.description, url: `${SITE_URL}${t.path}`, mainEntityOfPage: `${SITE_URL}${t.path}`, inLanguage: "fr-FR",
        author: FOUNDER, publisher: { "@id": ORGANIZATION["@id"] }, ...(t.image ? { image: absUrl(t.image.src) } : {}), ...(t.updated ? { datePublished: t.published || t.updated, dateModified: t.updated } : {}) },
    ] },
  });

  return (
    <main data-testid={`topic-page-${id}`} className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={crumbs} />
      <div className={t.image ? "grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_380px]" : ""}>
      <div>
      <p className="eyebrow mb-6">{t.eyebrow}</p>
      <h1 data-testid="topic-title" className="max-w-5xl font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl"><MaskLine delay={0.05}>{t.h1}</MaskLine></h1>
      <p data-testid="topic-intro" className="mt-8 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{t.intro}</p>
      <GuideByline updated={t.updated} />
      {t.answer ? (
        <div data-testid="topic-answer" className="mt-8 max-w-3xl border-l-2 border-[var(--gold)] bg-[var(--ink-2)] p-5 md:p-6">
          <p className="eyebrow mb-2">L'essentiel en bref</p>
          <p className="text-sm leading-relaxed text-[var(--parchment)] md:text-base">{t.answer}</p>
        </div>
      ) : null}
      {t.expertise ? <div className="mt-8 max-w-3xl"><ExpertiseBlock testid="topic-expertise" compact /></div> : null}
      {t.notice ? (
        <p data-testid="topic-medical-notice" className="mt-8 flex max-w-3xl gap-3 rounded-sm border border-[var(--gold)]/40 bg-[rgba(217,180,106,0.08)] p-5 text-sm leading-relaxed text-[var(--parchment)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" aria-hidden="true" />{t.notice}
        </p>
      ) : null}
      {repouss ? <PregnancyNotice testid="topic-pregnancy-notice" /> : null}
      {t.group === "formation" ? <FormationGuideCta testid="formation-guide-cta-top" className="mt-8 max-w-3xl" /> : null}
      </div>
      {t.image ? (
        <figure className="mx-auto w-full max-w-[320px] overflow-hidden rounded-t-full border border-[var(--line)] lg:max-w-none">
          <img data-testid="topic-image" src={t.image.src} alt={t.image.alt} width="671" height="1000" fetchPriority="high" className="aspect-[4/5] w-full object-cover object-top" />
          {t.group === "chute" ? <figcaption className="text-center"><PinButton path={t.path} image={t.image.src} text={`${t.h1} | Ozentya`} /></figcaption> : null}
        </figure>
      ) : null}
      </div>
      <div className="mt-16 grid gap-14 border-t border-[var(--line)] pt-16 lg:grid-cols-2">
        {t.sections.map((s) => (
          <section key={s.h2} className="max-w-2xl">
            <h2 className="font-display text-3xl leading-tight">{s.h2}</h2>
            <p className="mt-4 text-sm leading-relaxed text-[var(--parchment)]/85 md:text-base">{inline(s.p, s.h2)}</p>
            {(s.h3s || []).map((h) => (
              <div key={h.h3} className="mt-5">
                <h3 className="font-display text-xl italic text-[var(--gold-soft)]">{h.h3}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--parchment)]/85">{inline(h.p, h.h3)}</p>
              </div>
            ))}
          </section>
        ))}
      </div>
      {t.group === "formation" ? <FormationGuideCta testid="formation-guide-cta-bottom" className="mx-auto mt-20 max-w-4xl" /> : null}
      {id === CHUTE_HUB ? <CureBanner testid="hub-cure-banner" className="mt-24" /> : null}
      {products.length ? (
        <section data-testid="topic-products" className="mt-24">
          <h2 className="mb-3 font-display text-3xl italic sm:text-4xl">Les soins Ozentya adaptés</h2>
          <p className="mb-10 text-sm text-[var(--parchment)]/75">{SHIP}</p>
          <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((p, i) => <ProductCard key={p.slug} p={p} index={i} details />)}
          </div>
        </section>
      ) : null}
      {t.group === "chute" ? <SubGuideBanner path={t.path} /> : null}
      {chute ? <Testimonials className="-mx-5 mt-20 md:-mx-10" /> : null}
      {chute ? <GuideOffer source={id} /> : null}
      <Faq items={t.faq} />
      {chute ? <FormationBox src="guide" testid="guide-formation-box" className="mx-auto mt-16 max-w-4xl" /> : null}
      {articles.length ? (
        <section className="mt-24">
          <h2 className="mb-8 font-display text-3xl sm:text-4xl">À lire pour aller plus loin</h2>
          <ArticleCards articles={articles} />
        </section>
      ) : null}
      {chute ? <ChuteGuidesNav current={t.path} /> : null}
      {t.group === "expert" ? <ExpertGuidesNav current={t.path} /> : null}
      {t.group === "formation" ? <FormationGuidesNav current={t.path} /> : null}
      <section data-testid="topic-related" className="mt-20">
        <h2 className="eyebrow mb-5">Sujets liés</h2>
        <div className="flex flex-wrap gap-2">
          {[...t.related, "/conseils"].map((to) => (
            <Link key={to} to={to} data-testid={`topic-related-${to.slice(1)}`} className="flex h-10 items-center rounded-full border border-[var(--line)] px-5 text-xs uppercase tracking-[0.18em] text-[var(--parchment)]/85 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold-soft)]">
              {LABELS[to] || (to === "/faq-cheveux-afro" ? "FAQ cheveux afro" : "Conseils & routines")}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
